import React, { useState, useEffect, useCallback } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import Card from 'react-bootstrap/Card';
import Form from 'react-bootstrap/Form';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Alert from 'react-bootstrap/Alert';

const IntakeScreenVelocityCalculator = () => {
    const [flow, setFlow] = useState('');
    const [useCustom, setUseCustom] = useState(false);
    const [screenSize, setScreenSize] = useState('18');
    const [customLength, setCustomLength] = useState('');
    const [customWidth, setCustomWidth] = useState('');
    const [customHeight, setCustomHeight] = useState('');
    const [verticalWHSides, setVerticalWHSides] = useState(1);
    const [verticalLHSides, setVerticalLHSides] = useState(2);
    const [topOpen, setTopOpen] = useState(false);
    const [bottomOpen, setBottomOpen] = useState(false);
    const [screenVelocity, setScreenVelocity] = useState('');
    const [screenVelocityStatus, setScreenVelocityStatus] = useState('');
    const [flumeDiameter, setFlumeDiameter] = useState('');
    const [flumeVelocity, setFlumeVelocity] = useState('');
    const [flumeVelocityStatus, setFlumeVelocityStatus] = useState('');
    
    // Available screen sizes
    const screenSizes = [
        { value: '18', label: '18"' },
        { value: '24', label: '24"' },
        { value: '36', label: '36"' },
        { value: '48', label: '48"' }
    ];

    // Calculate screen velocity based on flow and effective screen size
    // Original Formula: (flow*0.1337/60)/(3*((screenSize/12)^2))
    // Adjustments:
    //  - Perimeter angle reduces each side by 4" → effectiveSize = screenSize - 4
    //  - Mesh open area is 69% → reduce open area, which increases velocity: divide base velocity by 0.69
    const calculateScreenVelocity = useCallback(() => {
        try {
            if (!flow || (!useCustom && !screenSize) || isNaN(flow) || (useCustom && (!customLength || !customWidth))) {
                setScreenVelocity('ENTER FLOW & SCREEN SIZE');
                setScreenVelocityStatus('');
                return;
            }
            // Convert inputs to numbers
            const flowNum = Number(flow);
            
            let effectiveArea; // in square feet
            
            if (!useCustom) {
                // Preset mode: assume square screen with 3 sides open
                const screenSizeNum = Number(screenSize);
                const effectiveSizeInches = Math.max(screenSizeNum - 4, 0);
                const effectiveSizeFeet = effectiveSizeInches / 12;
                effectiveArea = 3 * Math.pow(effectiveSizeFeet, 2);
            } else {
                // Custom mode: calculate area based on selected sides and top/bottom
                const lengthInches = Number(customLength);
                const widthInches = Number(customWidth);
                const heightInches = Number(customHeight);
                
                // Apply perimeter reduction: 4" per side
                const effectiveLengthInches = Math.max(lengthInches - 4, 0);
                const effectiveWidthInches = Math.max(widthInches - 4, 0);
                const effectiveHeightInches = Math.max(heightInches - 4, 0);
                
                const effectiveLengthFeet = effectiveLengthInches / 12;
                const effectiveWidthFeet = effectiveWidthInches / 12;
                const effectiveHeightFeet = effectiveHeightInches / 12;
                
                // Calculate area: vertical sides (W×H and L×H) + top/bottom (W×L)
                const whSideArea = verticalWHSides * (effectiveWidthFeet * effectiveHeightFeet);
                const lhSideArea = verticalLHSides * (effectiveLengthFeet * effectiveHeightFeet);
                const topBottomArea = (topOpen ? 1 : 0) * (effectiveWidthFeet * effectiveLengthFeet) +
                                       (bottomOpen ? 1 : 0) * (effectiveWidthFeet * effectiveLengthFeet);
                
                effectiveArea = whSideArea + lhSideArea + topBottomArea;
            }
            
            // Flow in cubic feet per second
            const flowCFS = flowNum * 0.1337 / 60;

            // Base velocity using effective area
            const baseVelocity = flowCFS / effectiveArea;

            // Apply mesh open area factor (69% open area → velocity increases)
            const adjustedVelocity = baseVelocity / 0.69;

            if (isNaN(adjustedVelocity) || !isFinite(adjustedVelocity)) {
                setScreenVelocity('Error: Invalid calculation');
                setScreenVelocityStatus('danger');
                return;
            }

            setScreenVelocity(adjustedVelocity.toFixed(2));
            
            // Set status based on guidelines - 1.5 ft/sec is the max recommended velocity
            if (adjustedVelocity <= 0.75) {
                setScreenVelocityStatus('success');
            } else if (adjustedVelocity <= 1.5) {
                setScreenVelocityStatus('warning');
            } else {
                setScreenVelocityStatus('danger');
            }
            
        } catch (error) {
            console.error('Screen velocity calculation error:', error);
            setScreenVelocity('Error');
            setScreenVelocityStatus('danger');
        }
    }, [flow, screenSize, useCustom, customLength, customWidth, customHeight, verticalWHSides, verticalLHSides, topOpen, bottomOpen]);

    // Calculate flume velocity based on flow and flume diameter
    const calculateFlumeVelocity = useCallback(() => {
        try {
            if (!flow || !flumeDiameter || isNaN(flow) || isNaN(flumeDiameter)) {
                setFlumeVelocity('ENTER FLOW & FLUME DIAMETER');
                setFlumeVelocityStatus('');
                return;
            }
            
            // Breaking down the calculation into simpler parts for debugging
            const flowCFS = Number(flow) * 0.1337 / 60; // Flow in cubic feet per second
            const radiusInches = Number(flumeDiameter) / 2; // Radius in inches
            const radiusFeet = radiusInches / 12; // Convert radius to feet
            const areaSquareFeet = Math.PI * Math.pow(radiusFeet, 2); // Area in square feet
            const velocity = flowCFS / areaSquareFeet; // Velocity in feet per second
            
            if (isNaN(velocity) || !isFinite(velocity)) {
                console.error('Invalid velocity calculation result:', {
                    flow, flumeDiameter, flowCFS, radiusInches, radiusFeet, areaSquareFeet, velocity
                });
                setFlumeVelocity('Error: Invalid calculation');
                setFlumeVelocityStatus('danger');
                return;
            }
            
            setFlumeVelocity(velocity.toFixed(2));
            
            // Set status based on guidelines - 1.5 ft/sec is the maximum
            if (velocity <= 1.0) {
                setFlumeVelocityStatus('success');
            } else if (velocity <= 1.5) {
                setFlumeVelocityStatus('warning');
            } else {
                setFlumeVelocityStatus('danger');
            }
            
        } catch (error) {
            console.error('Flume velocity calculation error:', error);
            setFlumeVelocity('Error');
            setFlumeVelocityStatus('danger');
        }
    }, [flow, flumeDiameter]);

    // Calculate both velocities when inputs change
    useEffect(() => {
        calculateScreenVelocity();
    }, [calculateScreenVelocity]);

    useEffect(() => {
        calculateFlumeVelocity();
    }, [calculateFlumeVelocity]);

    const getStatusClass = (status) => {
        switch(status) {
            case 'success':
                return 'bg-success text-white';
            case 'warning':
                return 'bg-warning';
            case 'danger':
                return 'bg-danger text-white';
            default:
                return 'bg-light';
        }
    };

    return (
        <div className="container-fluid py-3">
            <Row className="mb-4">
                <Col lg={{span: 8, offset: 2}} md={12}>
                    <Card className="shadow-sm">
                        <Card.Header as="h5" className="bg-primary text-white">
                            Intake Screen Velocity Calculator
                        </Card.Header>
                        
                        {/* Adding prominent note about maximum velocity */}
                        <Alert variant="danger" className="text-center m-0 py-2">
                            <strong>NOTE: VELOCITY MUST BE 1.5 FT/SEC OR LESS</strong>
                        </Alert>
                        
                        <Card.Body>
                            <Form>
                                <Row>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label htmlFor="flow"><strong>Flow Rate (GPM):</strong></Form.Label>
                                            <Form.Control
                                                type="number"
                                                id="flow"
                                                value={flow}
                                                onChange={(e) => setFlow(e.target.value)}
                                                placeholder="Enter flow rate"
                                                className="shadow-sm"
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>
                                
                                <Row className="mb-3">
                                    <Col md={12}>
                                        <Form.Check
                                            type="checkbox"
                                            id="useCustom"
                                            label="Use Custom Dimensions"
                                            checked={useCustom}
                                            onChange={(e) => setUseCustom(e.target.checked)}
                                        />
                                    </Col>
                                </Row>
                                
                                {!useCustom && (
                                <Row className="mb-4">
                                    <Col md={4}>
                                        <Form.Group>
                                            <Form.Label htmlFor="screenSize"><strong>Screen Size (inches):</strong></Form.Label>
                                            <Form.Select
                                                id="screenSize"
                                                value={screenSize}
                                                onChange={(e) => setScreenSize(e.target.value)}
                                                className="shadow-sm"
                                            >
                                                {screenSizes.map((size) => (
                                                    <option key={size.value} value={size.value}>
                                                        {size.label}
                                                    </option>
                                                ))}
                                            </Form.Select>
                                        </Form.Group>
                                    </Col>
                                    
                                    <Col md={8}>
                                        <div className="mb-3 mt-4 p-3 border rounded">
                                            <Row className="align-items-center">
                                                <Col md={6}>
                                                    <h6 className="mb-0">Screen Velocity:</h6>
                                                </Col>
                                                <Col md={6}>
                                                    {screenVelocity !== 'ENTER FLOW & SCREEN SIZE' && screenVelocity !== 'Error' ? (
                                                        <div className={`p-2 rounded ${getStatusClass(screenVelocityStatus)}`}>
                                                            <h5 className="mb-0 text-center">{screenVelocity} ft/sec</h5>
                                                        </div>
                                                    ) : (
                                                        <Alert variant="secondary" className="p-2 mb-0">
                                                            {screenVelocity}
                                                        </Alert>
                                                    )}
                                                </Col>
                                            </Row>
                                        </div>
                                    </Col>
                                </Row>
                                )}
                                
                                {useCustom && (
                                <Row className="mb-4">
                                    <Col md={4}>
                                        <Form.Group>
                                            <Form.Label htmlFor="customLength"><strong>Length (inches):</strong></Form.Label>
                                            <Form.Control
                                                type="number"
                                                id="customLength"
                                                value={customLength}
                                                onChange={(e) => setCustomLength(e.target.value)}
                                                placeholder="Enter length"
                                                className="shadow-sm"
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={4}>
                                        <Form.Group>
                                            <Form.Label htmlFor="customWidth"><strong>Width (inches):</strong></Form.Label>
                                            <Form.Control
                                                type="number"
                                                id="customWidth"
                                                value={customWidth}
                                                onChange={(e) => setCustomWidth(e.target.value)}
                                                placeholder="Enter width"
                                                className="shadow-sm"
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={4}>
                                        <Form.Group>
                                            <Form.Label htmlFor="customHeight"><strong>Height (inches):</strong></Form.Label>
                                            <Form.Control
                                                type="number"
                                                id="customHeight"
                                                value={customHeight}
                                                onChange={(e) => setCustomHeight(e.target.value)}
                                                placeholder="Enter height"
                                                className="shadow-sm"
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>
                                )}
                                
                                {useCustom && (
                                <Row className="mb-4">
                                    <Col md={6}>
                                        <Form.Group>
                                            <Form.Label htmlFor="customWHSides"><strong>W×H Sides:</strong></Form.Label>
                                            <Form.Control
                                                type="number"
                                                id="customWHSides"
                                                value={verticalWHSides}
                                                onChange={(e) => {
                                                    const newValue = Math.max(0, Number(e.target.value));
                                                    if (newValue + verticalLHSides <= 4) {
                                                        setVerticalWHSides(newValue);
                                                    }
                                                }}
                                                min="0"
                                                max="4"
                                                className="shadow-sm"
                                            />
                                        </Form.Group>
                                    </Col>
                                    
                                    <Col md={6}>
                                        <Form.Group>
                                            <Form.Label htmlFor="customLHSides"><strong>L×H Sides:</strong></Form.Label>
                                            <Form.Control
                                                type="number"
                                                id="customLHSides"
                                                value={verticalLHSides}
                                                onChange={(e) => {
                                                    const newValue = Math.max(0, Number(e.target.value));
                                                    if (verticalWHSides + newValue <= 4) {
                                                        setVerticalLHSides(newValue);
                                                    }
                                                }}
                                                min="0"
                                                max="4"
                                                className="shadow-sm"
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>
                                )}
                                
                                {useCustom && (
                                <Row className="mb-4">
                                    <Col md={6}>
                                        <Form.Check
                                            type="checkbox"
                                            id="topOpen"
                                            label="Top Open (W×L)"
                                            checked={topOpen}
                                            onChange={(e) => setTopOpen(e.target.checked)}
                                        />
                                    </Col>
                                    <Col md={6}>
                                        <Form.Check
                                            type="checkbox"
                                            id="bottomOpen"
                                            label="Bottom Open (W×L)"
                                            checked={bottomOpen}
                                            onChange={(e) => setBottomOpen(e.target.checked)}
                                        />
                                    </Col>
                                </Row>
                                )}
                                
                                {useCustom && (
                                <Row className="mb-4">
                                    <Col md={12}>
                                        <div className="mb-3 p-3 border rounded">
                                            <Row className="align-items-center">
                                                <Col md={6}>
                                                    <h6 className="mb-0">Screen Velocity:</h6>
                                                </Col>
                                                <Col md={6}>
                                                    {screenVelocity !== 'ENTER FLOW & SCREEN SIZE' && screenVelocity !== 'Error' ? (
                                                        <div className={`p-2 rounded ${getStatusClass(screenVelocityStatus)}`}>
                                                            <h5 className="mb-0 text-center">{screenVelocity} ft/sec</h5>
                                                        </div>
                                                    ) : (
                                                        <Alert variant="secondary" className="p-2 mb-0">
                                                            {screenVelocity}
                                                        </Alert>
                                                    )}
                                                </Col>
                                            </Row>
                                        </div>
                                    </Col>
                                </Row>
                                )}
                                
                                <hr className="my-4" />
                                
                                <Row className="mb-4">
                                    <Col md={4}>
                                        <Form.Group>
                                            <Form.Label htmlFor="flumeDiameter"><strong>Intake Flume Diameter (inches):</strong></Form.Label>
                                            <Form.Control
                                                type="number"
                                                id="flumeDiameter"
                                                value={flumeDiameter}
                                                onChange={(e) => setFlumeDiameter(e.target.value)}
                                                placeholder="Enter diameter"
                                                className="shadow-sm"
                                            />
                                        </Form.Group>
                                    </Col>
                                    
                                    <Col md={8}>
                                        <div className="mb-3 mt-4 p-3 border rounded">
                                            <Row className="align-items-center">
                                                <Col md={6}>
                                                    <h6 className="mb-0">Intake Flume Velocity:</h6>
                                                </Col>
                                                <Col md={6}>
                                                    {flumeVelocity !== 'ENTER FLOW & FLUME DIAMETER' && flumeVelocity !== 'Error' ? (
                                                        <div className={`p-2 rounded ${getStatusClass(flumeVelocityStatus)}`}>
                                                            <h5 className="mb-0 text-center">{flumeVelocity} ft/sec</h5>
                                                        </div>
                                                    ) : (
                                                        <Alert variant="secondary" className="p-2 mb-0">
                                                            {flumeVelocity}
                                                        </Alert>
                                                    )}
                                                </Col>
                                            </Row>
                                        </div>
                                    </Col>
                                </Row>
                            </Form>
                            
                            <Alert variant="info" className="mt-3">
                                <strong>Guidelines:</strong>
                                <ul className="mb-0">
                                    <li><strong>Maximum allowed velocity: 1.5 ft/sec</strong></li>
                                </ul>
                            </Alert>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </div>
    );
};

export default IntakeScreenVelocityCalculator;